import { Autocomplete, Grid, Popper, TextField } from '@mui/material';
import React from 'react';

import { ContextStore } from '../store/contextStore';
import { DEFAULT_LANG, LANGUAGES, defaultLang, useIsWidthUp } from '../utils';

const localizedTextSetFieldPropsAreEqual = (prev, next) =>
    prev.name === next.name &&
    prev.disabled === next.disabled &&
    prev.label === next.label &&
    prev.value === next.value &&
    prev.onChange === next.onChange &&
    prev.variant === next.variant &&
    prev.size === next.size &&
    prev.fullWidth === next.fullWidth &&
    prev.required === next.required &&
    prev.expanded === next.expanded &&
    prev.containerDirectionOverride === next.containerDirectionOverride &&
    prev.omitLanguageInLabel === next.omitLanguageInLabel;

const styles = (theme) => ({
    popper: {
        width: 'fit-content',
    },
});

const PopperMy = function (props) {
    return <Popper {...props} style={styles.popper} placement="bottom-start" />;
};

const AbbrLanguageDropdown = (props) => {
    const onChange = (ev, nv, reason) => {
        // console.log('AbbrLanguageDropdown', ev, nv, reason);
        props.onChange && props.onChange(nv);
    };

    return (
        <Autocomplete
            PopperComponent={PopperMy}
            disabled={props.disabled}
            disablePortal
            options={[...props.list].sort((a, b) => String(a.code).localeCompare(String(b.code)))}
            groupBy={(opt) => opt.empty}
            getOptionLabel={(opt) => opt.code}
            // The options are copies (with the empty marker) made on every render: match by code, not identity.
            isOptionEqualToValue={(opt, val) => opt.code === val.code}
            renderOption={(props, opt, state, ownerState) => (
                <li {...props}>
                    {opt.code} - {opt.name}
                </li>
            )}
            sx={{
                marginRight: '2px',
                '.MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                },
            }}
            renderInput={(params) => <TextField {...params} variant={props.variant} size={props.size} label={props.label} fullWidth={props.fullWidth} />}
            onChange={onChange}
            value={props.selected}
        />
    );
};

function LocalizedTextSetFieldInner(props) {
    // This component's own props stay here: spread onto the TextField they reached the DOM, and React
    // warned ("does not recognize the `containerDirectionOverride` prop", "non-boolean attribute `expanded`").
    const { containerDirectionOverride, expanded, omitLanguageInLabel, ...fieldProps } = props;
    const { tourOperator } = React.useContext(ContextStore);
    const i18n = tourOperator.config?.i18n || {};
    const allowed_langs = i18n.lang_enabled ? LANGUAGES.filter((x) => i18n.lang_enabled.indexOf(x.code) >= 0) : LANGUAGES.filter((x) => x.code === DEFAULT_LANG);
    // The editor opens on the tenant's default language: the LAST enabled one (AIE D21).
    const default_lang_code = defaultLang(i18n.lang_enabled, DEFAULT_LANG);
    const default_lang_list = allowed_langs.filter((x) => x.code === default_lang_code);
    const default_lang = default_lang_list.length === 1 ? default_lang_list[0] : { code: default_lang_code, name: 'Default' };
    const langs_with_empty_marker = allowed_langs?.map((x) => ({ ...x, group: (!props.value?.values || !props.value?.values[x.code]) ? -1 : 1 }));
    const nonempty_lang_count = langs_with_empty_marker.filter((x) => x.group >= 0).length;
    const [selectedLang, setSelectedLang] = React.useState(default_lang);
    const isSmallScreen = !useIsWidthUp('sm');

    const [isMissingRequiredField, setIsMissingRequiredField] = React.useState(false);

    const onSelectedLangChange = (nv) => {
        setSelectedLang(nv ? nv : default_lang);
    };

    const validateField = (ev) => {
        props.required && setIsMissingRequiredField(!ev.target.value);
    };

    const onTextChange = (ev) => {
        validateField(ev);
        // console.log('lt: onTextChange', ev.target.name, ev.target.value);
        props.onChange &&
            props.onChange({
                target: {
                    name: props.name,
                    value: {
                        ...props.value,
                        values: {
                            ...props.value?.values,
                            [ev.target.name]: ev.target.value,
                        },
                    },
                },
            });
    };

    const text_label = !omitLanguageInLabel ? `${props.label} (${selectedLang.name})` : props.label;
    const locale = new Intl.Locale(selectedLang.code);
    let textInfo = null;
    try {
        textInfo = locale.getTextInfo();
    } catch (e) {
        textInfo = locale.textInfo;
    }
    const dir = textInfo ? textInfo.direction : 'ltr';
    return expanded ? (
        <Grid container direction={isSmallScreen ? "column" : "row"} spacing={1} alignItems="flex-start">
            { langs_with_empty_marker.map((lang, idx) => (<React.Fragment key={idx}>
                <Grid item xs={12}>
                    <TextField
                        {...fieldProps}
                        multiline={!!props.value?.multiline}
                        name={lang.code}
                        label={lang.name}
                        value={props.value?.values?.[lang.code] || ''}
                        onChange={onTextChange}
                        onBlur={validateField}
                        inputProps={{
                            dir: dir,
                        }}
                        fullWidth
                        error={props.required && isMissingRequiredField}
                        helperText={props.required ? (isMissingRequiredField && 'This is a required field') : ''}
                    />
                </Grid>
            </React.Fragment>)) }
        </Grid>
    ) : (
        <Grid container direction={isSmallScreen ? containerDirectionOverride || "column" : "row"} spacing={1} alignItems="flex-start">
            <Grid item xs={12} sm="auto" style={{ width: isSmallScreen ? '100%' : '75px' }}>
                <AbbrLanguageDropdown
                    selected={selectedLang}
                    onChange={onSelectedLangChange}
                    list={langs_with_empty_marker}
                    size={props.size}
                    variant={props.variant}
                    fullWidth
                    label={`${nonempty_lang_count}/${langs_with_empty_marker.length}`}
                    disabled={props.disabled}
                />
            </Grid>
            <Grid item xs={12} sm style={{ width: '100%' }}>
                <TextField
                    {...fieldProps}
                    multiline={!!props.value?.multiline}
                    name={selectedLang.code}
                    label={text_label}
                    value={props.value?.values?.[selectedLang.code] || ''}
                    onChange={onTextChange}
                    onBlur={validateField}
                    inputProps={{
                        dir: dir,
                    }}
                    fullWidth
                    error={props.required && isMissingRequiredField}
                    helperText={props.required ? (isMissingRequiredField && 'This is a required field') : ''}
                />
            </Grid>
        </Grid>
    );

    // return (<TextField
    //     {...props}
    //     value={props.value.values[selectedLang]}
    //     InputProps={{
    //         startAdornment: <AbbrLanguageDropdown selected={selectedLang} onChange={onSelectedLangChange} list={langs_with_empty_marker} variant={props.variant}/> // label={`${nonempty_lang_count}/${langs_with_empty_marker.length}`}/>
    //     }}
    // />);
}

export const LocalizedTextSetField = React.memo(LocalizedTextSetFieldInner, localizedTextSetFieldPropsAreEqual);

export default LocalizedTextSetField;
